import { Badge, Modal, Table } from "react-bootstrap";

function formatLabel(key: string) {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatScalar(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.map(String).join(", ") : "—";
  return String(value);
}

function DetailTable({ rows }: { rows: Array<[string, unknown]> }) {
  if (!rows.length) return <p className="text-muted mb-0">No data available.</p>;

  return (
    <Table responsive size="sm" className="mb-0">
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key}>
            <td className="text-muted" style={{ width: "38%" }}>
              {formatLabel(key)}
            </td>
            <td className="fw-semibold">{formatScalar(value)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AddressBlock({ address }: { address?: Record<string, unknown> }) {
  if (!address || typeof address !== "object") return null;

  return (
    <div className="border rounded-3 p-3 mb-3">
      <h6 className="mb-2">Address</h6>
      <DetailTable
        rows={Object.entries(address).filter(([, value]) => typeof value !== "object")}
      />
    </div>
  );
}

function OwnerCard({ owner, index }: { owner: Record<string, unknown>; index: number }) {
  const residence = owner.residenceAddress as Record<string, unknown> | undefined;
  const scalarRows = Object.entries(owner).filter(
    ([key, value]) => key !== "residenceAddress" && typeof value !== "object"
  );

  return (
    <div className="border rounded-3 p-3 mb-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">{String(owner.type || `Owner ${index + 1}`)}</h6>
        {owner.ownershipPercentage !== undefined && (
          <Badge bg="light" text="dark">
            {formatScalar(owner.ownershipPercentage)}% ownership
          </Badge>
        )}
      </div>
      <DetailTable rows={scalarRows} />
      <AddressBlock address={residence} />
    </div>
  );
}

export function normalizeApplicationDetail(application: unknown) {
  if (!application || typeof application !== "object") return null;
  const record = application as Record<string, unknown>;
  if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
    return record.data as Record<string, unknown>;
  }
  return record;
}

type Props = {
  show: boolean;
  onHide: () => void;
  application: unknown;
};

export default function ApplicationDetailModal({ show, onHide, application }: Props) {
  const detail = normalizeApplicationDetail(application);
  if (!detail) return null;

  const businessProfile = detail.businessProfile as Record<string, unknown> | undefined;
  const transactionInfo = detail.transactionInfo as Record<string, unknown> | undefined;
  const owners = Array.isArray(detail.owners) ? detail.owners : [];

  const topLevelRows = Object.entries(detail).filter(
    ([key, value]) =>
      !["businessProfile", "transactionInfo", "owners"].includes(key) &&
      typeof value !== "object"
  );

  const businessRows = businessProfile
    ? Object.entries(businessProfile).filter(
        ([key, value]) => key !== "businessAddress" && typeof value !== "object"
      )
    : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Application Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {topLevelRows.length > 0 && (
          <div className="border rounded-3 p-3 mb-3">
            <h6 className="mb-2">Application</h6>
            <DetailTable rows={topLevelRows} />
          </div>
        )}

        {businessProfile && (
          <div className="border rounded-3 p-3 mb-3">
            <h6 className="mb-2">Business Profile</h6>
            <DetailTable rows={businessRows} />
            <AddressBlock address={businessProfile.businessAddress as Record<string, unknown>} />
          </div>
        )}

        {transactionInfo && (
          <div className="border rounded-3 p-3 mb-3">
            <h6 className="mb-2">Transaction Info</h6>
            <DetailTable
              rows={Object.entries(transactionInfo).filter(([, value]) => typeof value !== "object")}
            />
          </div>
        )}

        {owners.length > 0 && (
          <div>
            <h6 className="mb-2">Owners</h6>
            {owners.map((owner, index) => (
              <OwnerCard key={index} owner={owner as Record<string, unknown>} index={index} />
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
